package com.solevo.dist.pages

import androidx.compose.runtime.*
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.viewmodels.RewardsUiState
import com.solevo.dist.viewmodels.RewardsViewModel
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.Path
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.theme.styles.sectionTitle

@Page("/rewards")
@Composable
fun RewardsScreen() {
    RequireAuth { RewardsPage() }
}

@Composable
private fun VSpace(h: CSSLengthValue) = Box(Modifier.height(h))

@Composable
fun RewardsPage() {
    WaitForAuthAndViewModel(
        key = "rewards",
        viewModelFactory = { RewardsViewModel() },
        onViewModelReady = { vm -> vm.start() }
    ) { vm ->
        DisposableEffect(Unit) { onDispose { vm.stop() } }
        val ui by vm.state.collectAsState()

        DashboardPageLayout(header = { RewardsHeader() }) {
            RewardsBody(ui)
        }
    }
}

@Composable
private fun RewardsHeader() {
    Column(Modifier.fillMaxWidth().padding(24.px)) {
        SpanText(com.solevo.dist.i18n.LocalStrings.current.rewardsTitle, Modifier.sectionTitle())
    }
}

@Composable
private fun RewardsBody(ui: RewardsUiState) {
    Column(Modifier.fillMaxWidth().padding(24.px)) {
        val strings = LocalStrings.current
        Row(
            Modifier
                .fillMaxWidth()
                .gap(24.px)
                .styleModifier { property("flex-wrap", "wrap") }
        ) {
            // Left section: Rebates
            CardSection(title = strings.rewardsRebates) {
                Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    Column(
                        Modifier
                            .padding(12.px)
                            .borderRadius(12.px)
                            .alignItems(AlignItems.Center)
                    ) {
                        SpanText(
                            ui.accruedRebateText,
                            Modifier.fontSize(28.px).fontWeight(FontWeight.Bold).color(rgb(36, 41, 47))
                        )
                        VSpace(4.px)
                        SpanText(strings.rewardsAccruedRebate, Modifier.fontSize(12.px).color(rgb(120, 130, 140)))
                    }
                }

                VSpace(16.px)
                Column(Modifier.fillMaxWidth()) {
                    ui.rebateRows.forEachIndexed { index, row ->
                        RebateRow(
                            label = row.label,
                            value = row.valueText.ifBlank { "—" },
                            last = index == ui.rebateRows.lastIndex
                        )
                    }
                }
            }

            // Right section: Credit Score
            CardSection(title = strings.rewardsCreditScore) {
                Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    CreditScoreGauge(
                        score = ui.creditScore,
                        max = ui.maxCreditScore,
                        gapLabel = strings.rewardsCreditScoreLabel
                    ) {
                        Row(Modifier.alignItems(AlignItems.Center).gap(4.px)) {
                            SpanText(
                                ui.creditScore.toString(),
                                Modifier.fontSize(28.px).fontWeight(FontWeight.Bold).color(rgb(36, 41, 47))
                            )
                            SpanText("/" + ui.maxCreditScore, Modifier.fontSize(12.px).color(rgb(120, 130, 140)))
                        }
                    }
                }

                VSpace(16.px)
                Column(Modifier.fillMaxWidth()) {
                    SummaryRow(strings.rewardsPaymentTerms, ui.paymentTermsText)
                    SummaryRow(strings.rewardsCreditLine, ui.creditLineText, last = true)
                }
            }
        }
    }
}

@Composable
private fun CardSection(
    title: String,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(Modifier.width(420.px)) {
        SpanText(title, Modifier.fontSize(18.px).fontWeight(FontWeight.Bold))
        VSpace(12.px)
        Card { content() }
    }
}

@Composable
private fun Card(modifier: Modifier = Modifier, content: @Composable ColumnScope.() -> Unit) {
    Column(
        modifier
            .fillMaxWidth()
            .backgroundColor(rgb(255, 255, 255))
            .border(1.px, LineStyle.Solid, rgb(232, 235, 240))
            .borderRadius(12.px)
            .padding(16.px)
    ) { content() }
}

@Composable
private fun RebateRow(label: String, value: String, last: Boolean) {
    Column(Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth().padding(topBottom = 12.px)) {
            SpanText(label, Modifier.fontSize(13.px).color(rgb(120, 130, 140)).flexGrow(1.0))
            SpanText(value, Modifier.fontSize(14.px).color(rgb(36, 41, 47)))
        }
        if (!last) Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(rgb(240, 242, 246)))
    }
}

@Composable
private fun SummaryRow(label: String, value: String, last: Boolean = false) {
    Column(Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth().padding(topBottom = 12.px)) {
            SpanText(label, Modifier.fontSize(13.px).color(rgb(120, 130, 140)).flexGrow(1.0))
            SpanText(value, Modifier.fontSize(14.px).color(rgb(36, 41, 47)))
        }
        if (!last) Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(rgb(240, 242, 246)))
    }
}

@Composable
private fun CreditScoreGauge(
    score: Int,
    max: Int,
    size: CSSLengthValue = 220.px,
    gapLabel: String? = null,
    centerContent: (@Composable () -> Unit)? = null
) {
    val percent = remember(score, max) { (score.coerceAtLeast(0).coerceAtMost(max)).toDouble() / max.toDouble() }
    val startAngle = 225.0
    val span = 270.0
    val endAngleBg = startAngle + span
    val endAngleFg = startAngle + span * percent

    val stroke = 18.0
    val dim = 220.0
    val cx = dim / 2
    val cy = dim / 2
    val r = (dim - stroke) / 2

    Box(Modifier.size(size).position(Position.Relative), contentAlignment = Alignment.Center) {
        Svg({
            attr("width", dim.toInt().toString())
            attr("height", dim.toInt().toString())
            style {
                property("overflow", "visible")
            }
        }) {
            // Track arc
            Path {
                attr("d", arcPath(cx, cy, r, startAngle, endAngleBg))
                attr("fill", "none")
                attr("stroke", "#E9EDF3")
                attr("stroke-width", stroke.toString())
                attr("stroke-linecap", "round")
            }

            // Progress arc
            Path {
                attr("d", arcPath(cx, cy, r, startAngle, endAngleFg))
                attr("fill", "none")
                attr("stroke", "#2B6FF3")
                attr("stroke-width", stroke.toString())
                attr("stroke-linecap", "round")
            }
        }
        if (centerContent != null) {
            Box(
                Modifier
                    .position(Position.Absolute)
                    .left(0.px).right(0.px).top(0.px).bottom(0.px)
                    .display(DisplayStyle.Flex)
                    .alignItems(AlignItems.Center)
                    .justifyContent(JustifyContent.Center)
            ) { centerContent() }
        }
        if (gapLabel != null) {
            // Place the label into the open gap (bottom center)
            Box(
                Modifier
                    .position(Position.Absolute)
                    .left(0.px).right(0.px)
                    .bottom(24.px)
                    .display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center)
            ) {
                SpanText(gapLabel, Modifier.fontSize(12.px).color(rgb(120, 130, 140)))
            }
        }
    }
}

private fun arcPath(cx: Double, cy: Double, r: Double, startAngle: Double, endAngle: Double): String {
    val start = polarToCartesian(cx, cy, r, endAngle)
    val end = polarToCartesian(cx, cy, r, startAngle)
    val largeArcFlag = if ((endAngle - startAngle) % 360.0 > 180.0) 1 else 0
    return "M ${start.first} ${start.second} A $r $r 0 $largeArcFlag 0 ${end.first} ${end.second}"
}

private fun polarToCartesian(cx: Double, cy: Double, r: Double, angleDeg: Double): Pair<Double, Double> {
    val rad = (angleDeg - 90.0) * (kotlin.math.PI / 180.0)
    val x = cx + (r * kotlin.math.cos(rad))
    val y = cy + (r * kotlin.math.sin(rad))
    return Pair(x, y)
}
